import React, { useState } from 'react';
import { sendMessage } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useSocialLinks } from '../context/SocialContext';

const Contact: React.FC = () => {
  const { t } = useTranslation();
  const { socialLinks } = useSocialLinks();
  const [formState, setFormState] = useState({ name: '', email: '', subject: t('contact.subject'), message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await sendMessage({
        name: formState.name,
        email: formState.email,
        subject: formState.subject,
        body: formState.message
      });
      setSubmitted(true);
      setFormState({ name: '', email: '', subject: 'Destek Talebi', message: '' });
    } catch (error: any) {
      console.error(error);
      alert(`${t('contact.error_prefix')} ${error.message || t('contact.error_default')}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen py-24 overflow-hidden animate-in fade-in duration-1000">
      {/* Abstract Background Shapes */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-[10%] right-[-5%] w-[400px] h-[400px] bg-brand-purple-100 rounded-full blur-[100px] opacity-40"></div>
        <div className="absolute bottom-[5%] left-[-10%] w-[500px] h-[500px] bg-brand-green-50 rounded-full blur-[120px] opacity-50"></div>
        <div className="absolute top-[40%] left-[20%] w-[150px] h-[150px] bg-brand-yellow-400 rounded-full blur-[80px] opacity-20"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">

          {/* Text & Contact Info Section */}
          <div className="space-y-12">
            <div className="space-y-6">
              <span className="text-brand-purple-500 font-bold uppercase tracking-widest text-sm">{t('contact.subtitle')}</span>
              <h1 className="text-5xl md:text-6xl font-serif font-bold text-brand-purple-900 leading-tight">
                {t('contact.title')}
              </h1>
              <p className="text-xl text-gray-600 font-light leading-relaxed max-w-lg">
                {t('contact.description')}
              </p>
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-white p-8 rounded-[2rem] border border-brand-purple-50 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-brand-purple-100 rounded-2xl flex items-center justify-center text-brand-purple-600 mb-6">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h4 className="font-bold text-brand-purple-900 mb-2">{t('contact.email_title')}</h4>
                <a href="mailto:tja.bilgi@gmail.com" className="text-sm text-gray-500 hover:text-brand-purple-600 transition-colors block">tja.bilgi@gmail.com</a>
              </div>

            </div>

            {/* Social Links */}
            <div className="space-y-4">
              <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">{t('contact.social_title')}</p>
              <div className="flex flex-wrap gap-3">
                {[
                  { 
                    id: 'x', 
                    title: 'X: @NavendTJA', 
                    url: socialLinks.x, 
                    icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg> 
                  },
                  { 
                    id: 'instagram', 
                    title: 'Instagram: @tjanavend', 
                    url: socialLinks.instagram, 
                    icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" /></svg> 
                  },
                  { 
                    id: 'tiktok', 
                    title: 'TikTok: @tjamerkez', 
                    url: socialLinks.tiktok, 
                    icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.24 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg> 
                  },
                  { 
                    id: 'youtube', 
                    title: 'YouTube: @tjatevgerajinenazad7984', 
                    url: socialLinks.youtube, 
                    icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z" clipRule="evenodd" /></svg> 
                  },
                  { 
                    id: 'facebook', 
                    title: 'Facebook: Tevgera Jinên Azad', 
                    url: socialLinks.facebook, 
                    icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" /></svg> 
                  }
                ].map((platform) => (
                  <a 
                    key={platform.id} 
                    href={platform.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    title={platform.title}
                    className="w-11 h-11 rounded-2xl border border-brand-purple-100 flex items-center justify-center text-brand-purple-700 hover:bg-brand-purple-600 hover:text-white transition-all transform hover:scale-110 shadow-sm active:scale-95"
                  >
                    {platform.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form Section */}
          <div className="bg-white p-10 lg:p-14 rounded-[3.5rem] shadow-2xl shadow-brand-purple-900/5 border border-brand-purple-50 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-yellow-400/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2"></div>

            <h3 className="text-3xl font-serif font-bold text-brand-purple-900 mb-8">{t('contact.send_message')}</h3>

            {submitted ? (
              <div className="py-20 text-center space-y-4 animate-in zoom-in duration-500">
                <div className="w-20 h-20 bg-brand-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-brand-green-500/20">
                  <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="text-2xl font-bold text-brand-purple-900">{t('contact.success_title')}</h4>
                <p className="text-gray-500">{t('contact.success_desc')}</p>
                <button onClick={() => setSubmitted(false)} className="text-brand-purple-600 font-bold hover:underline mt-4">{t('contact.new_message')}</button>
              </div>
            ) : (
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-2">{t('contact.name')}</label>
                  <input
                    required
                    type="text"
                    value={formState.name}
                    onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                    className="w-full bg-brand-neutral-bg border-2 border-transparent focus:border-brand-purple-200 focus:bg-white rounded-2xl px-6 py-4 outline-none transition-all placeholder:text-gray-300"
                    placeholder={t('contact.form_name_placeholder') as string}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-2">{t('contact.email')}</label>
                  <input
                    required
                    type="email"
                    value={formState.email}
                    onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                    className="w-full bg-brand-neutral-bg border-2 border-transparent focus:border-brand-purple-200 focus:bg-white rounded-2xl px-6 py-4 outline-none transition-all placeholder:text-gray-300"
                    placeholder={t('contact.form_email_placeholder') as string}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-2">{t('contact.subject')}</label>
                  <select
                    value={formState.subject}
                    onChange={(e) => setFormState({ ...formState, subject: e.target.value })}
                    className="w-full bg-brand-neutral-bg border-2 border-transparent focus:border-brand-purple-200 focus:bg-white rounded-2xl px-6 py-4 outline-none transition-all appearance-none cursor-pointer"
                  >
                    <option>{t('contact.opt_support')}</option>
                    <option>{t('contact.opt_volunteer')}</option>
                    <option>{t('contact.opt_legal')}</option>
                    <option>{t('contact.opt_collab')}</option>
                    <option>{t('contact.opt_general')}</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-2">{t('contact.message')}</label>
                  <textarea
                    required
                    rows={5}
                    value={formState.message}
                    onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                    className="w-full bg-brand-neutral-bg border-2 border-transparent focus:border-brand-purple-200 focus:bg-white rounded-2xl px-6 py-4 outline-none transition-all placeholder:text-gray-300 resize-none"
                    placeholder={t('contact.form_message_placeholder') as string}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-brand-purple-900 text-white py-5 rounded-2xl font-bold text-xl hover:bg-brand-purple-700 hover:shadow-xl hover:shadow-brand-purple-900/20 transition-all active:scale-[0.98] mt-4 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center"
                >
                  {isSubmitting ? (
                    <div className="h-6 w-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : t('contact.send_button')}
                </button>
                <p className="text-[10px] text-center text-gray-400 italic">
                  {t('contact.kvkk')}
                </p>
              </form>
            )}
          </div>
        </div>


      </div>
    </div>
  );
};

export default Contact;
